import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faImage,
  faGraduationCap,
  faSpinner,
  faPaperPlane,
  faCheck,
  faEye,
  faEyeSlash,
  faArrowLeft,
  faArrowRight,
  faEnvelope,
  faPhone,
  faVenusMars,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import ApiServices from "../Services/ApiServices";
import Loader from "../Shared/Loader";
import FileUploader from "../Shared/FileUploader";
import { Dropdown, DropdownItem } from "../Shared/Dropdown/Dropdown";
import { useNavigate } from "react-router-dom";
import { toastHelper } from "../../Helpers/toastHelper";
import extractErrorMessage from "../../Helpers/APIHelper";
import logoIcon from "../../assets/images/new_log_design.svg";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";

const genders = [
  { value: 1, label: "ذكر" },
  { value: 2, label: "أنثى" },
];

const step1Schema = Yup.object().shape({
  fullName: Yup.string()
    .matches(/^[\u0621-\u064Aa-zA-Z\s]+$/, "الاسم يجب أن يحتوي على أحرف فقط")
    .max(400, "الاسم يجب أن لا يتجاوز 400 حرف")
    .required("الاسم بالكامل مطلوب"),
  email: Yup.string()
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "بريد إلكتروني غير صالح")
    .required("البريد الإلكتروني مطلوب"),
  password: Yup.string()
    .min(8, "كلمة المرور يجب أن تكون 8 أحرف على الأقل")
    .matches(/[A-Z]/, "يجب أن تحتوي على حرف كبير")
    .matches(/[a-z]/, "يجب أن تحتوي على حرف صغير")
    .matches(/[0-9]/, "يجب أن تحتوي على رقم")
    .required("كلمة المرور مطلوبة"),
  confirmPassword: Yup.string()
    .required("تأكيد كلمة المرور مطلوب")
    .oneOf([Yup.ref("password"), null], "كلمتا المرور غير متطابقتين"),
  phone: Yup.string()
    .matches(/^01[0-9]{9}$/, "رقم هاتف غير صحيح")
    .required("رقم الهاتف مطلوب"),
  gender: Yup.string().required("النوع مطلوب"),
});

const step2Schema = Yup.object().shape({
  qualification: Yup.string().required("المؤهل العلمي مطلوب"),
  about: Yup.string().max(300, "الوصف لا يجب أن يتجاوز 300 حرف"),
  material: Yup.object().nullable().required("اختر المادة"),
  classes: Yup.array().min(1, "اختر صفاً واحداً على الأقل"),
});

const validationSchema = step1Schema.concat(step2Schema);

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.21, 1, 0.36, 1] },
  }),
};

const RegisterAsTeacher = () => {
  const [loading, setLoading] = useState(false);
  const [profilePic, setProfilePic] = useState(null);
  const [classesList, setClassesList] = useState([]);
  const [materialsList, setMaterialsList] = useState([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classes, materials] = await Promise.all([
          ApiServices.get("Lookup/UserClasses"),
          ApiServices.get("Lookup/Materials"),
        ]);
        setClassesList(classes);
        setMaterialsList(materials);
        if (materials.length > 0) {
          setSelectedMaterial({
            id: materials[0].id,
            name: materials[0].name,
            description: materials[0].name,
          });
        }
      } catch (error) {

      } finally {
        setListsLoading(false);
      }
    };
    fetchData();
  }, []);

  const initialValues = {
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    about: "",
    qualification: "",
    gender: "",
    material: null,
    classes: [],
  };

  const handleImageChange = (e, setFieldValue) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePic(file);
      setFieldValue("profile_picture", file);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!values.classes || values.classes.length === 0) {
      toastHelper.error("يرجى اختيار صف واحد على الأقل");
      setSubmitting(false);
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(values).forEach(key => {
        if (key === 'material') formData.append('material_id', values[key].id);
        else if (key === 'classes') values[key].forEach(id => formData.append('user_class', id));
        else if (key !== 'confirmPassword') formData.append(key, values[key]);
      });
      if (profilePic) formData.append("profile_picture", profilePic);

      await ApiServices.post("User/Teacher/Add", formData, true, 300000);
      toastHelper.success("تم إرسال طلبك بنجاح! سيتم التواصل معك قريباً.");
      navigate("/otp", { state: { email: values.email, phone: values.phone, personal_name: values.fullName } });
      resetForm();
    } catch (error) {
      const msg = extractErrorMessage(error);
      toastHelper.error(msg || "حدث خطأ أثناء إرسال الطلب");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: "البيانات الأساسية", icon: faUser },
    { number: 2, title: "البيانات الأكاديمية", icon: faGraduationCap },
    { number: 3, title: "الصورة الشخصية", icon: faImage },
  ];

  if (listsLoading) return <Loader text="جاري تحميل البيانات..." />;

  return (
    <div className="rt-root">
      <div className="rt-layout">
        {/* Left Side (Info Panel) */}
        <div className="rt-info">
          <div className="rt-info-content">
            <motion.div
              className="rt-logo-box"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img src={logoIcon} alt="Logo" className="rt-logo" />
            </motion.div>

            <motion.div
              className="rt-badge"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="rt-badge-dot" />
              بوابة المعلمين
            </motion.div>

            <motion.h1
              className="rt-title"
              initial="hidden"
              animate="visible"
              custom={1}
              variants={fadeUp}
            >
              انضم لنخبة <br /><em>المعلمين المتميزين</em>
            </motion.h1>

            <motion.p
              className="rt-desc"
              initial="hidden"
              animate="visible"
              custom={2}
              variants={fadeUp}
            >
              شارك معرفتك وخبراتك مع آلاف الطلاب. منصة تعليمية متكاملة توفر لك كل الأدوات التي تحتاجها للنجاح والتميز.
            </motion.p>

            <div className="rt-features">
              {[
                { icon: faCheck, text: "أدوات تقييم ومتابعة متطورة" },
                { icon: faCheck, text: "تواصل مباشر مع الطلاب" },
                { icon: faCheck, text: "مرونة كاملة في إدارة الوقت" }
              ].map((f, i) => (
                <motion.div
                  key={i}
                  className="rt-feature"
                  initial="hidden"
                  animate="visible"
                  custom={3 + i}
                  variants={fadeUp}
                >
                  <div className="rt-feature-icon"><FontAwesomeIcon icon={f.icon} /></div>
                  <span>{f.text}</span>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="rt-footer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              transition={{ delay: 1 }}
            >
              <p>© 2026 جميع الحقوق محفوظة لمنصة امتحاني</p>
            </motion.div>
          </div>
          <div className="rt-info-bg" />
        </div>

        {/* Right Side (Form Panel) */}
        <div className="rt-form-panel">
          <div className="rt-form-container">
            {/* Mobile Header */}
            <div className="rt-mobile-header">
              <h2>تسجيل حساب معلم</h2>
              <p>خطوة واحدة تفصلك عن البدء</p>
            </div>

            <div className="rt-hint">
              <FontAwesomeIcon icon={faInfoCircle} className="rt-hint-icon" />
              <span>هذا التسجيل مخصص للمعلمين فقط وليس للطلاب.</span>
            </div>

            {/* Stepper */}
            <div className="rt-stepper">
              {steps.map((step, idx) => (
                <div key={step.number} className={`rt-step ${currentStep >= step.number ? 'active' : ''} ${currentStep === step.number ? 'current' : ''}`}>
                  <div className="rt-step-circle">
                    {currentStep > step.number ? <FontAwesomeIcon icon={faCheck} /> : step.number}
                  </div>
                  <span className="rt-step-label">{step.title}</span>
                  {idx < steps.length - 1 && <div className="rt-step-line" />}
                </div>
              ))}
            </div>

            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
              {({ values, errors, touched, setFieldValue }) => (
                <Form className="rt-form" autoComplete="off">
                  <AnimatePresence mode="wait">
                    {/* Step 1: Personal Info */}
                    {currentStep === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="rt-step-content"
                      >
                        <div className="rt-field-group">
                          <label>الاسم بالكامل</label>
                          <div className="rt-input-box">
                            <Field name="fullName" className={`rt-input ${touched.fullName && errors.fullName ? 'err' : ''}`} placeholder="الاسم كما يظهر في الهوية" autoComplete="off" />
                            <FontAwesomeIcon icon={faUser} className="rt-field-icon" />
                          </div>
                          <ErrorMessage name="fullName" component="div" className="rt-err" />
                        </div>

                        <div className="rt-field-group">
                          <label>البريد الإلكتروني</label>
                          <div className="rt-input-box">
                            <Field name="email" type="email" className={`rt-input ${touched.email && errors.email ? 'err' : ''}`} placeholder="address@example.com" autoComplete="off" />
                            <FontAwesomeIcon icon={faEnvelope} className="rt-field-icon" />
                          </div>
                          <ErrorMessage name="email" component="div" className="rt-err" />
                        </div>

                        <div className="rt-row">
                          <div className="rt-field-group">
                            <label>كلمة المرور</label>
                            <div className="rt-input-box">
                              <Field name="password" type={showPassword ? "text" : "password"} className={`rt-input ${touched.password && errors.password ? 'err' : ''}`} placeholder="••••••••" autoComplete="new-password" />
                              <button type="button" className="rt-pw-toggle" onClick={() => setShowPassword(!showPassword)}>
                                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                              </button>
                            </div>
                            <ErrorMessage name="password" component="div" className="rt-err" />
                          </div>
                          <div className="rt-field-group">
                            <label>تأكيد كلمة المرور</label>
                            <div className="rt-input-box">
                              <Field name="confirmPassword" type={showConfirmPassword ? "text" : "password"} className={`rt-input ${touched.confirmPassword && errors.confirmPassword ? 'err' : ''}`} placeholder="••••••••" autoComplete="new-password" />
                              <button type="button" className="rt-pw-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                              </button>
                            </div>
                            <ErrorMessage name="confirmPassword" component="div" className="rt-err" />
                          </div>
                        </div>

                        <div className="rt-row">
                          <div className="rt-field-group">
                            <label>رقم الهاتف</label>
                            <div className="rt-input-box">
                              <Field name="phone" className={`rt-input ${touched.phone && errors.phone ? 'err' : ''}`} placeholder="01xxxxxxxxx" autoComplete="off" />
                              <FontAwesomeIcon icon={faPhone} className="rt-field-icon" />
                            </div>
                            <ErrorMessage name="phone" component="div" className="rt-err" />
                          </div>
                          <div className="rt-field-group">
                            <label>النوع</label>
                            <div className="rt-input-box">
                              <Field as="select" name="gender" className={`rt-input ${touched.gender && errors.gender ? 'err' : ''}`}>
                                <option value="">اختر..</option>
                                {genders.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                              </Field>
                              <FontAwesomeIcon icon={faVenusMars} className="rt-field-icon" />
                            </div>
                            <ErrorMessage name="gender" component="div" className="rt-err" />
                          </div>
                        </div>

                        <div className="rt-nav">
                          <button type="button" onClick={() => navigate('/')} className="rt-back-btn">عودة للرئيسية</button>
                          <button
                            type="button"
                            className="rt-next-btn"
                            onClick={() => setCurrentStep(2)}
                            disabled={!step1Schema.isValidSync(values)}
                          >
                            <span>التالي</span>
                            <FontAwesomeIcon icon={faArrowLeft} />
                          </button>
                        </div>
                      </motion.div>
                    )}

                    {/* Step 2: Academic Info */}
                    {currentStep === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="rt-step-content"
                      >
                        <div className="rt-field-group">
                          <label>المؤهل العلمي</label>
                          <div className="rt-input-box">
                            <Field name="qualification" className={`rt-input ${touched.qualification && errors.qualification ? 'err' : ''}`} placeholder="مثال: بكالوريوس تربية - قسم لغة عربية" autoComplete="off" />
                            <FontAwesomeIcon icon={faGraduationCap} className="rt-field-icon" />
                          </div>
                          <ErrorMessage name="qualification" component="div" className="rt-err" />
                        </div>

                        <div className="rt-field-group">
                          <label>المادة التي تدرسها</label>
                          <Dropdown
                            width="100%"
                            searchable={true}
                            trigger={
                              <div className="rt-input-box" style={{ width: '100%' }}>
                                <button
                                  type="button"
                                  className={`rt-input ${touched.material && errors.material ? 'err' : ''}`}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    textAlign: 'right',
                                    backgroundColor: '#fff',
                                    border: '1.5px solid rgba(15,23,42,0.1)',
                                    borderRadius: '14px',
                                    padding: '13px 18px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <span>{selectedMaterial?.name || "اختر المادة"}</span>
                                  <FontAwesomeIcon icon={faChevronDown} style={{ color: '#94a3b8' }} />
                                </button>
                              </div>
                            }
                          >
                            {materialsList.map((m) => (
                              <DropdownItem
                                key={m.id}
                                onClick={() => {
                                  setSelectedMaterial(m);
                                  setFieldValue('material', { id: m.id, name: m.name });
                                }}
                              >
                                {m.name}
                              </DropdownItem>
                            ))}
                          </Dropdown>
                          {touched.material && errors.material && (
                            <div className="rt-err">{errors.material}</div>
                          )}
                        </div>

                        <div className="rt-field-group">
                          <label>الصفوف الدراسية</label>
                          <div className="rt-classes-grid">
                            {classesList.map(cls => (
                              <label key={cls.id} className={`rt-class-chip ${values.classes.includes(cls.id) ? 'active' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={values.classes.includes(cls.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) setFieldValue("classes", [...values.classes, cls.id]);
                                    else setFieldValue("classes", values.classes.filter(id => id !== cls.id));
                                  }}
                                />
                                <span>{cls.name}</span>
                              </label>
                            ))}
                          </div>
                          <ErrorMessage name="classes" component="div" className="rt-err" />
                        </div>

                        <div className="rt-field-group">
                          <label>نبذة عنك (اختياري)</label>
                          <Field as="textarea" name="about" className="rt-input rt-textarea" rows="3" placeholder="خبراتك السابقة، أسلوبك في التدريس..." />
                        </div>

                        <div className="rt-nav">
                          <button type="button" className="rt-prev-btn" onClick={() => setCurrentStep(1)}>
                            <FontAwesomeIcon icon={faArrowRight} /> السابق
                          </button>
                          <button
                            type="button"
                            className="rt-next-btn"
                            onClick={() => setCurrentStep(3)}
                            disabled={!step2Schema.isValidSync(values)}
                          >
                            <span>التالي</span>
                            <FontAwesomeIcon icon={faArrowLeft} />
                          </button>
                        </div>
                      </motion.div>
                    )}

                    {/* Step 3: Profile Pic */}
                    {currentStep === 3 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="rt-step-content"
                      >
                        <div className="rt-upload-box">
                          <h3>صورة الملف الشخصي</h3>
                          <p>أضف صورة احترافية لتظهر للطلاب (اختياري)</p>

                          <FileUploader
                            previewUrl={profilePic ? URL.createObjectURL(profilePic) : null}
                            onFileChange={(e) => handleImageChange(e, setFieldValue)}
                            accept="image/*"
                            placeholder="اضغط لرفع الصورة"
                            id="profile_picture"
                          />
                        </div>

                        <div className="rt-nav">
                          <button type="button" className="rt-prev-btn" onClick={() => setCurrentStep(2)}>
                            <FontAwesomeIcon icon={faArrowRight} /> السابق
                          </button>
                          <button
                            type="submit"
                            className="rt-submit-btn"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>جاري الإرسال..</span>
                              </>
                            ) : (
                              <>
                                <span>إتمام التسجيل</span>
                                <FontAwesomeIcon icon={faPaperPlane} />
                              </>
                            )}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      <style jsx>{`
        .rt-root {
          min-height: 100vh;
          background: #fff;
            font-family: "Zain", sans-serif;
          overflow-x: hidden;
        }

        .rt-layout {
          display: flex;
          min-height: 100vh;
        }

        /* Left Side Info Panel */
        .rt-info {
          width: 42%;
          background: #0f172a;
          position: relative;
          color: white;
          padding: 60px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow: hidden;
        }

        .rt-info-bg {
          position: absolute;
          inset: 0;
          background: 
            radial-gradient(circle at 10% 20%, rgba(99, 102, 241, 0.1) 0%, transparent 40%),
            radial-gradient(circle at 90% 80%, rgba(232, 146, 10, 0.05) 0%, transparent 40%);
          z-index: 1;
        }

        .rt-info-content {
          position: relative;
          z-index: 2;
          max-width: 520px;
        }

        .rt-logo-box { margin-bottom: 40px; }
        .rt-logo { width: 130px; height: auto; }

        .rt-badge {
          display: inline-flex; align-items: center; gap: 10px;
          background: rgba(255,255,255,0.06);
          padding: 8px 18px; border-radius: 50px;
          margin-bottom: 28px; font-size: 0.85rem; font-weight: 700;
          color: #94a3b8; border: 1px solid rgba(255,255,255,0.1);
        }
        .rt-badge-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: rt-pulse 2.2s ease-in-out infinite;
        }
        @keyframes rt-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(232,146,10,0.4); }
          50% { box-shadow: 0 0 0 8px rgba(232,146,10,0); }
        }

        .rt-title {
          font-size: clamp(2.5rem, 4vw, 3.8rem);
          font-weight: 900; line-height: 1.15;
          margin-bottom: 28px; color: #fff;
        }
        .rt-title em {
          font-style: normal;
          background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .rt-desc {
          color: #94a3b8; font-size: 1.1rem; line-height: 1.7;
          margin-bottom: 48px;
        }

        .rt-features {
          display: flex; flex-direction: column; gap: 20px;
        }
        .rt-feature {
          display: flex; align-items: center; gap: 16px;
          color: #cbd5e1; font-weight: 600;
        }
        .rt-feature-icon {
          width: 34px; height: 34px; background: rgba(99, 102, 241, 0.15);
          border-radius: 50%; display: flex; align-items: center;
          justify-content: center; color: #6366f1; font-size: 0.9rem;
        }

        .rt-footer {
          margin-top: 80px; font-size: 0.9rem;
        }

        /* Right Side Form Panel */
        .rt-form-panel {
          width: 58%; padding: 60px;
          display: flex; align-items: center; justify-content: center;
          background: #fff;
        }

        .rt-form-container { width: 100%; max-width: 640px; }

        .rt-hint {
          background: #fffbeb; border: 1px solid #fef3c7;
          padding: 16px 20px; border-radius: 16px;
          color: #92400e; display: flex; align-items: center;
          gap: 14px; margin-bottom: 36px; font-size: 0.95rem;
          font-weight: 700; border-right: 4px solid #6366f1;
        }
        .rt-hint-icon { color: #6366f1; font-size: 1.2rem; }

        .rt-mobile-header { display: none; text-align: center; margin-bottom: 32px; }
        .rt-mobile-header h2 { font-size: 2.2rem; color: #0f172a; font-weight: 900; }

        /* Stepper */
        .rt-stepper {
          display: flex; justify-content: space-between;
          margin-bottom: 56px; position: relative;
        }
        .rt-step {
          display: flex; flex-direction: column; align-items: center;
          position: relative; z-index: 2; flex: 1;
        }
        .rt-step-circle {
          width: 44px; height: 44px; border-radius: 50%;
          background: #fff; border: 2px solid #e2e8f0;
          display: flex; align-items: center; justify-content: center;
          font-weight: 800; color: #94a3b8; margin-bottom: 10px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .rt-step.active .rt-step-circle { border-color: #6366f1; color: #6366f1; background: #f5f3ff; }
        .rt-step.current .rt-step-circle {
          background: #6366f1; border-color: #6366f1; color: #fff;
          box-shadow: 0 0 0 5px rgba(99, 102, 241, 0.15);
        }
        .rt-step-label { font-size: 0.88rem; color: #64748b; font-weight: 600; }
        .rt-step.active .rt-step-label { color: #0f172a; font-weight: 800; }
        .rt-step-line {
          position: absolute; top: 22px; right: 50%; left: -50%;
          height: 2px; background: #e2e8f0; z-index: -1;
        }
        .rt-step.active + .rt-step .rt-step-line { background: #6366f1; }

        /* Form Styling */
        .rt-field-group { margin-bottom: 28px; }
        .rt-field-group label {
          display: block; margin-bottom: 10px; color: #334155;
          font-weight: 700; font-size: 0.95rem;
        }
        .rt-input-box { position: relative; }
        .rt-input {
          width: 100%; padding: 16px 48px 16px 20px;
          border: 1.5px solid #e2e8f0; border-radius: 14px;
          background: #fff; font-size: 1rem; transition: all 0.3s;
          color: #0f172a; font-family: inherit; font-weight: 600;
        }
        .rt-input:focus {
          border-color: #6366f1; box-shadow: 0 0 0 4px rgba(232, 146, 10, 0.08);
          outline: none;
        }
        .rt-input.err { border-color: #ef4444; background: #fffcfc; }
        .rt-field-icon {
          position: absolute; right: 18px; top: 50%;
          transform: translateY(-50%); color: #94a3b8;
          transition: color 0.3s; z-index: 5; pointer-events: none;
        }
        .rt-input:focus + .rt-field-icon { color: #6366f1; }

        .rt-row { display: flex; gap: 24px; }
        .rt-row > * { flex: 1; }

        .rt-pw-toggle {
          position: absolute; left: 16px; top: 50%;
          transform: translateY(-50%); background: none;
          border: none; color: #94a3b8; cursor: pointer;
          z-index: 10; padding: 5px; display: flex;
        }

        .rt-classes-grid { display: flex; flex-wrap: wrap; gap: 12px; }
        .rt-class-chip {
          padding: 10px 22px; background: #fff; border: 1.5px solid #e2e8f0;
          border-radius: 50px; cursor: pointer; transition: all 0.25s;
          font-size: 0.9rem; color: #64748b; font-weight: 700;
        }
        .rt-class-chip input { display: none; }
        .rt-class-chip.active {
          border-color: #6366f1; background: #fffbeb; color: #6366f1;
        }

        .rt-upload-box {
          text-align: center; padding: 48px; border: 2px dashed #e2e8f0;
          border-radius: 20px; background: #fbfbfc; margin-bottom: 32px;
        }
        .rt-upload-box h3 { color: #0f172a; font-weight: 800; margin-bottom: 10px; }
        .rt-upload-box p { color: #64748b; margin-bottom: 28px; }

        .rt-nav {
          display: flex; justify-content: space-between;
          align-items: center; margin-top: 48px;
        }
        .rt-next-btn, .rt-submit-btn {
          padding: 16px 36px; background: #6366f1; color: #fff;
          border: none; border-radius: 14px; font-weight: 800;
          font-size: 1rem; cursor: pointer; display: flex;
          align-items: center; gap: 12px; transition: all 0.3s;
          box-shadow: 0 10px 20px -5px rgba(99, 102, 241, 0.4);
        }
        .rt-next-btn:hover, .rt-submit-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 30px -8px rgba(99, 102, 241, 0.5);
        }
        .rt-next-btn:disabled { background: #cbd5e1; cursor: not-allowed; transform: none; box-shadow: none; }

        .rt-prev-btn {
          background: none; border: none; color: #64748b;
          font-weight: 800; font-size: 1rem; cursor: pointer;
          display: flex; align-items: center; gap: 10px; transition: color 0.2s;
        }
        .rt-prev-btn:hover { color: #0f172a; }

        .rt-back-btn { background: none; border: none; color: #94a3b8; font-weight: 700; cursor: pointer; }

        .rt-err { color: #ef4444; font-size: 0.82rem; font-weight: 600; margin-top: 6px; }

        @media (max-width: 1150px) {
          .rt-info { width: 35%; padding: 40px; }
          .rt-form-panel { width: 65%; padding: 40px; }
        }

        @media (max-width: 1024px) {
          .rt-info { display: none; }
          .rt-form-panel { width: 100%; padding: 32px 24px; }
          .rt-mobile-header { display: block; }
        }

        @media (max-width: 640px) {
          .rt-row { flex-direction: column; gap: 0; }
          .rt-step-label { display: none; }
          .rt-stepper { margin-bottom: 40px; }
          .rt-next-btn, .rt-submit-btn { width: 100%; justify-content: center; }
          .rt-nav { flex-direction: column-reverse; gap: 24px; }
        }
      `}</style>
    </div>
  );
};

export default RegisterAsTeacher;
